import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Url } from '@rossum/api-client';
import { TablerIcon } from '@rossum/ui/icons/tabler';
import { SvgIcon } from '@rossum/ui/material';
import {
  GridActionsCellItem,
  GridActionsColDef,
  GridColDef,
  GridRowParams,
} from '@rossum/ui/x-data-grid-pro';
import { dateValueGetter } from '../../document-list-base/helpers/dateValueGetter';
import { SupportedAnnotationView } from '../../document-list-base/supportedAnnotationViews';
import { SimpleColumnsLocation } from '../../document-list-base/types';
import { TransformedAnnotation } from '../../document-list-columns/hooks/useTransformAnnotation';
import { useUnpaginatedWorklowRuns } from '../../document-list-columns/hooks/useUnpaginatedWorkflowRuns';
import {
  RestrictedAccessIcon,
  restrictedAccessIconLabel,
} from '../../pricing/components/RestrictedAccessIcon';
import { labelsFeatureSelector } from '../../pricing/selectors';
import { RenderDate } from '../components/RenderDate';
import { RenderDetails } from '../components/RenderDetails';
import { RenderStatus } from '../components/RenderStatus';
import { SimpleFilename } from '../components/SimpleFilename';
import { SimpleLabels } from '../components/SimpleLabels';
import { SimpleQueue } from '../components/SimpleQueue';
import { SimpleColumnWidths } from './useSimpleColumnWidths';

const LABELS_WIDTH = 150;

export const ACTIONS_COLUMN = 'actions';

const defaultColumnConfig = {
  filterable: false,
  sortable: false,
  pinnable: false,
  disableReorder: true,
};

type Props = {
  removeProps?: {
    onRemove: (annotationUrl: Url) => void;
    dataCyPrefix: string;
    Icon: TablerIcon;
    disableButton?: (annotation: TransformedAnnotation) => boolean;
  };
  columnsLocation?: SimpleColumnsLocation;
  columnWidths?: SimpleColumnWidths;
  detailsColumnProps?: {
    handleSelectAnnotation: (params: {
      annotationUrl: string;
      view: SupportedAnnotationView;
    }) => void;
    annotations: TransformedAnnotation[];
  };
};

export type SimpleColumnFieldNames =
  | 'status'
  | 'original_file_name'
  | 'queueName'
  | 'labels'
  | 'createdAt'
  | 'actions'
  | 'details';

export type SimpleColumn = Omit<GridColDef<TransformedAnnotation>, 'field'> & {
  field: SimpleColumnFieldNames;
  getActions?: GridActionsColDef['getActions'];
};

export const useSimpleColumns = ({
  removeProps,
  columnsLocation,
  columnWidths,
  detailsColumnProps,
}: Props = {}) => {
  const intl = useIntl();
  const labelsEnabled = useSelector(labelsFeatureSelector);

  const { data: workflowRuns } = useUnpaginatedWorklowRuns({
    annotationIds: detailsColumnProps?.annotations?.map(a => a.id) ?? [],
  });

  const columns = R.pipe<Array<SimpleColumn | undefined>, Array<SimpleColumn>>(
    [
      {
        field: 'status',
        width: 140,
        renderCell: RenderStatus,
        headerName: intl.formatMessage({
          id: 'components.documentOverview.status',
        }),
      },
      {
        field: 'original_file_name',
        headerName: intl.formatMessage({
          id: 'components.documentOverview.document__original_file_name',
        }),
        flex: 1,
        renderCell: props => (
          <SimpleFilename
            {...props}
            shouldShowAttachmentWarning={columnsLocation === 'attachmentsModal'}
          />
        ),
      },
      detailsColumnProps && {
        field: 'details',
        headerName: intl.formatMessage({
          id: 'components.documentOverview.details',
        }),
        width: 140,
        renderCell: props => {
          const hasWorkflowRun =
            workflowRuns?.some(
              ({ annotation }) => annotation === props.row.url
            ) ?? false;

          return (
            <RenderDetails
              {...props}
              hasWorkflowRun={hasWorkflowRun}
              isExcludedFromTraining={props.row.trainingEnabled === false}
              handleSelectAnnotation={detailsColumnProps.handleSelectAnnotation}
              componentLocation={columnsLocation}
              row={{
                ...props.row,
                hasAttachments: props.row.hasAttachmentRelation,
              }}
            />
          );
        },
      },
      {
        field: 'queueName',
        headerName: intl.formatMessage({
          id: 'components.documentOverview.queue',
        }),
        flex: 1,
        minWidth: 145,
        renderCell: SimpleQueue,
      },
      labelsEnabled
        ? {
            field: 'labels',
            headerName: intl.formatMessage({
              id: 'components.documentOverview.labels',
            }),
            width: LABELS_WIDTH,
            renderCell: SimpleLabels,
          }
        : undefined,
      {
        field: 'createdAt',
        headerName: intl.formatMessage({
          id: 'components.documentOverview.createdAtAlt',
        }),
        minWidth: 120,
        renderCell: RenderDate,
        valueGetter: dateValueGetter,
      },
      {
        field: ACTIONS_COLUMN,
        type: 'actions',
        flex: 0,
        align: 'right',
        getActions: ({ row }: GridRowParams<TransformedAnnotation>) => {
          if (!removeProps)
            return R.filter(
              // oxlint-disable-next-line react/jsx-key -- oxlint stricter than eslint — conditional branch in array
              [row.restricted_access ? <RestrictedAccessIcon /> : null],
              R.isNonNullish
            );

          const { onRemove, dataCyPrefix, Icon, disableButton } = removeProps;

          return R.filter(
            [
              // oxlint-disable-next-line react/jsx-key -- oxlint stricter than eslint — conditional branch in array
              row.restricted_access ? <RestrictedAccessIcon /> : null,
              <GridActionsCellItem
                key={`actions-${row.id}`}
                data-cy={`${dataCyPrefix}-remove-${row.id}`}
                icon={
                  <SvgIcon fontSize="medium">
                    <Icon />
                  </SvgIcon>
                }
                onClick={() => {
                  onRemove?.(row.url);
                }}
                label="Remove"
                disabled={disableButton?.(row) ?? false}
                className={
                  row.restricted_access ? restrictedAccessIconLabel : ''
                }
              />,
            ],
            R.isNonNullish
          );
        },
      },
    ],
    R.filter(R.isTruthy)
  );

  return columns.map(col => {
    const storedWidth = columnWidths?.[col.field];

    return {
      ...defaultColumnConfig,
      ...col,
      ...(storedWidth !== undefined ? { width: storedWidth, flex: 0 } : {}),
    };
  });
};
